import { Form } from 'antd';
import { isEmpty } from 'lodash-es';
import React, { FC, useEffect } from 'react';
import { isAndroid } from '@/infrastructure/product-info';
import { FormEntry, FormType, QueryForm } from '@/presentation/components/form';
import {
  DEFAULT_MONITOR_LIMIT_DAYS,
  DEFAULT_TIME_RANGE_ISSUES_MINUTE5,
} from '@/presentation/components/form/components/time-range';
import { VersionSelectGroupName } from '@/presentation/components/form/components/version-select-group';
import { FilterEntry, FormColumnGroup } from '@/presentation/components/form/types';
import { getVersionLabel } from '@/presentation/components/form/utils';
import { BUNDLE_TIPS_CASE, hintText } from '@/presentation/components/hint-text/hintText';
import { EXCEPTION_TYPE_OPTION } from '@/presentation/pages/battery/issues/config';
import {
  AppVersionOperator,
  FIELD_ALL,
  FIELD_LABEL,
  FieldKey,
  getLocalField,
  TagFilter,
  Zone,
} from '@/proto/field';
import { IssueType } from '@/proto/issue';
import { TimeType } from '@/proto/metric';
import { CompareFieldList } from '@/proto/token';

interface IProps {
  onFinish: (value: Record<string, any>) => void;
  entry: FilterEntry;
  initialValue?: CompareFieldList & { timeRange: string[], isValueInit?: boolean };
  onValueInited?: (value: Record<string, any>) => void;
}

// 兼容详情组件
export const PowerDetailProblemForm: FC<IProps> = (props: IProps) => {
  const { entry, onFinish } = props;
  const [form] = Form.useForm();


  useEffect(() => {
    if (!isEmpty(props.initialValue)) {
      const { initialValue } = props;
      const { isValueInit = true } = initialValue;
      form.setFieldsValue({
        ...initialValue,
        [FieldKey.ExceptionFilter]: initialValue[FieldKey.ExceptionFilter] ?? TagFilter.Or });
      if (isValueInit) {
        props.onValueInited?.(form.getFieldsValue());
      }
    }
  }, [props.initialValue]);


  const entries: FormEntry[] = [
    {
      type: FormType.TimeRangeCascade,
      name: 'timeRange',
      labelString: FIELD_LABEL[FieldKey.TimeRange],
      label: FIELD_LABEL[FieldKey.TimeRange],
      scope: 'issues',
      deleteable: false,
      initRange: DEFAULT_TIME_RANGE_ISSUES_MINUTE5,
      limitDays: DEFAULT_MONITOR_LIMIT_DAYS,
      showTime: true,
      initTimeWindow: TimeType.MINUTE5,
      hiddenTimeWindow: entry !== FilterEntry.PowerExceptionTypeList,
      isIssuesTimeWindow: entry === FilterEntry.PowerExceptionTypeList,
    },
    {
      type: FormType.VersionSelectGroup,
      leftName: FieldKey.AppVersionOperator,
      rightName: FieldKey.AppVersion,
      label: FIELD_LABEL[FieldKey.AppVersionOperator],
      leftFieldKey: FieldKey.AppVersionOperator,
      rightFieldKey: FieldKey.AppVersion,
      fieldScope: Zone.PowerAnomalyAnalysis,
      scope: 'issues',
      mode: 'tags',
      leftInitialValue: AppVersionOperator.Or,
      rightInitialValue: [FIELD_ALL],
      name: VersionSelectGroupName.appVersionGroup,
      deleteable: false,
      hasMaxWidth: true,
    },
    {
      type: FormType.Select,
      name: 'bundleId',
      label: hintText(FIELD_LABEL[FieldKey.BundleId], BUNDLE_TIPS_CASE),
      labelString: FIELD_LABEL[FieldKey.BundleId],
      fieldKey: FieldKey.BundleId,
      fieldScope: Zone.PowerAnomalyAnalysis,
      mode: 'single',
      initialValue: FIELD_ALL,
      group: FormColumnGroup.ApplicationInfo,
    },
    {
      type: FormType.SelectGroup,
      name: FieldKey.PowerExceptionType,
      leftName: FieldKey.ExceptionFilter,
      rightName: FieldKey.ExceptionTag,
      label: FIELD_LABEL[FieldKey.PowerExceptionType],
      rightOptions: EXCEPTION_TYPE_OPTION,
      leftOptions: getLocalField(FieldKey.PowerExceptionType) || [],
      mode: 'multiple',
      leftInitialValue: TagFilter.Or,
      group: FormColumnGroup.exceptionInfo,
    },
    {
      type: FormType.VersionSelect,
      name: FieldKey.OsVersion,
      versionName: FieldKey.OsVersion,
      label: getVersionLabel(FIELD_LABEL[FieldKey.OsVersion]),
      fieldKey: FieldKey.OsVersion,
      labelString: FIELD_LABEL[FieldKey.OsVersion],
      fieldScope: Zone.PowerAnomalyAnalysis,
      group: FormColumnGroup.DeviceInfo,
    },
    {
      type: FormType.Input,
      name: 'brand',
      label: FIELD_LABEL[FieldKey.Brand],
      disabled: !isAndroid(),
      group: FormColumnGroup.DeviceInfo,
    },
    {
      type: FormType.Select,
      name: 'sdkVersion',
      label: FIELD_LABEL[FieldKey.SdkVersion],
      fieldKey: FieldKey.SdkVersion,
      fieldScope: Zone.PowerAnomalyAnalysis,
      mode: 'single',
      initialValue: FIELD_ALL,
      dropdownMatchSelectWidth: 300,
      group: FormColumnGroup.ApplicationInfo,
    },
    {
      type: FormType.AutoComplete,
      name: 'model',
      label: FIELD_LABEL[FieldKey.Model],
      fieldKey: FieldKey.Model,
      fieldScope: Zone.PowerAnomalyAnalysis,
      initialValue: FIELD_ALL,
      group: FormColumnGroup.DeviceInfo,
    },
    {
      type: FormType.AutoComplete,
      name: FieldKey.Scenes,
      label: FIELD_LABEL[FieldKey.Scene],
      fieldKey: FieldKey.Scenes,
      fieldScope: Zone.PowerAnomalyAnalysis,
      initialValue: FIELD_ALL,
      dropdownMatchSelectWidth: 500,
      group: FormColumnGroup.ContextInfo,
    },
    {
      type: FormType.Select,
      name: 'processName',
      label: FIELD_LABEL[FieldKey.ProcessName],
      fieldKey: FieldKey.ProcessName,
      fieldScope: Zone.PowerAnomalyAnalysis,
      mode: 'single',
      initialValue: FIELD_ALL,
      disabled: !isAndroid(),
      group: FormColumnGroup.exceptionInfo,
    },
    {
      type: FormType.Input,
      name: 'qimei',
      label: '设备ID',
      group: FormColumnGroup.UserInfo,
    },
    {
      type: FormType.Input,
      name: 'accountId',
      label: '用户ID',
      group: FormColumnGroup.UserInfo,
    },
    {
      type: FormType.Input,
      name: 'launchId',
      label: FIELD_LABEL[FieldKey.LaunchId],
      labelString: '启动ID',
      group: FormColumnGroup.ContextInfo,
    },
    {
      type: FormType.Input,
      name: 'processLaunchId',
      label: FIELD_LABEL[FieldKey.ProcessLaunchId],
      labelString: '进程启动ID',
      disabled: !isAndroid(),
      group: FormColumnGroup.ContextInfo,
    },
    {
      type: FormType.CustomFields,
      name: FieldKey.UserCustom,
      label: '自定义维度',
      group: FormColumnGroup.Custom,
    },
  ];

  return <>
    <QueryForm
      form={form}
      entries={entries}
      onFinish={onFinish}
      pageEntry={entry}
      hasCompare={false}
      issueType={IssueType.ISSUE_TYPE_BATTERY_PROBLEM}
      hiddenCompareButton={true}
      cachePrefix={'prefix-1'}
      isSaveSearchValues={false}
      formProps={{ preserve: false }}
      renderByGroup
    />
  </>;
};
